import { AppDispatch } from '@/app/store';
import { AttachFileViewDTO } from '@/types/attachment/AttachFileViewDTO';
import { AttachmentStatus } from '@/types/attachment/AttachmentStatus';
import { AuthorDTO } from '@/types/AuthorDTO';
import { Box, Divider, Grid, Skeleton, Stack, Typography, useMediaQuery } from '@mui/material';
import { EnclosureViewDTO } from '@/types/enclosure/EnclosureViewDTO';
import { ImagePreview } from '@/components/view/image/ImagePreview';
import { ImageView, ImageViewItem, useImageView } from '@/app/hooks/useImageView';
import { ImageViewDialog } from '@/components/view/image/ImageViewDialog';
import { LetterHistory } from '@/components/letter/history/LetterHistory';
import { LetterView } from '@/components/letter/LetterView';
import { LoadingButton } from '@/components/common/button/LoadingButton';
import { MobileLetterHistory } from '@/components/letter/history/MobileLetterHistory';
import { PageHeader } from '@/components/common/PageHeader';
import {
  ROUTE_CREATE_LETTER_AUTHOR,
  ROUTE_CREATE_LETTER_CLIENT,
  ROUTE_INBOX,
  getCreateLetterReplyRoute,
} from '@/app/routes';
import { SenderInfoDTO } from '@/types/create/SenderInfoDTO';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import { VerdictDot } from '@/components/letter/VerdictDot';
import { formatPhotoLabel, formatUserFullName } from '@/utils/string-utils';
import { formatShortDateTimeString } from '@/utils/date-utils';
import { getCurrentUser } from '@/services/authSlice';
import { setAuthor, setCreateInfoFromDTO, setPhoneNumber } from '@/services/createFormSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useGetAnswerQuery } from '@/services/api/answerApiSlice';
import { useGetCurrentUserQuery } from '@/services/api/authApiSlice';
import { useGetEventLogQuery } from '@/services/api/eventApiSlice';
import { useGetLetterQuery } from '@/services/api/letterApiSlice';
import { useGetPhotoMutation } from '@/services/api/attachFileApiSlice';
import { useGetSenderToReplyMutation } from '@/services/api/createFormApiSlice';
import { useNavigate, useParams } from 'react-router-dom';
import { validateAuthorFull, validateSender } from '@/utils/validation-utils';
import React, { FC, Fragment, ReactElement } from 'react';
import type { NavigateFunction } from 'react-router-dom';

type Params = {
  id: string;
};

export const InboxLetterPage: FC = (): ReactElement => {
  const { id } = useParams<Params>();
  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();

  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const letterId = Number(id);

  const { data: user } = useGetCurrentUserQuery(authUser?.id, {
    skip: !authUser,
  });
  const { data: answer, isLoading: isAnswerLoading, isFetching: isAnswerFetching } = useGetAnswerQuery(letterId);
  const { data: letter, isFetching, isLoading } = useGetLetterQuery(letterId);
  const { data: events, isLoading: isEventLogLoading, isFetching: isEventLogFetching } = useGetEventLogQuery(letterId);

  const [getSender, { isLoading: isSenderLoading }] = useGetSenderToReplyMutation();
  const [getPhoto] = useGetPhotoMutation();

  const letterLoading: boolean = isFetching || isLoading;
  const answerLoading: boolean = isAnswerLoading || isAnswerFetching;
  const eventLogLoading: boolean = isEventLogLoading || isEventLogFetching;

  const imageView: ImageView = useImageView();

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  /**
   * Opens one photo full screen, reading its original only for the photo that was opened.
   *
   * @param photos The photos of the letter, in the order the page draws them.
   * @param index The position within that list of the photo the sender clicked.
   */
  const handleAttachFileClick = async (photos: AttachFileViewDTO[], index: number): Promise<void> => {
    await imageView.openList(
      photos.map(
        (attachFile: AttachFileViewDTO): ImageViewItem => ({
          name: formatPhotoLabel(attachFile.photoNumber, attachFile.photoIndex, photos.length),
          declined: attachFile.status === AttachmentStatus.DECLINED,
          read: async (): Promise<string | undefined> => {
            const photo: AttachFileViewDTO = await getPhoto(attachFile.id).unwrap();
            return photo?.content;
          },
        }),
      ),
      index,
    );
  };

  const handleReplyClick = async (): Promise<void> => {
    await getSender(letterId)
      .unwrap()
      .then((dto: SenderInfoDTO): void => {
        let author: AuthorDTO | null = null;
        dispatch(setCreateInfoFromDTO(dto));
        if (user) {
          author = {
            name: formatUserFullName(user),
            phone: user.phone || '',
            email: user.email,
          };
          dispatch(setAuthor(author));
          if (!user?.phone) {
            dispatch(setPhoneNumber(dto.authorPhone));
          }
        }
        if (!validateSender(dto.sender)) {
          navigate(ROUTE_CREATE_LETTER_CLIENT);
        } else if (!validateAuthorFull(author)) {
          navigate(ROUTE_CREATE_LETTER_AUTHOR);
        } else {
          navigate(getCreateLetterReplyRoute(letterId));
        }
      });
  };

  return (
    <Fragment>
      <Grid container={true} direction={'column'} wrap={'nowrap'} spacing={5}>
        <Grid item={true}>
          <Grid container={true} direction={'column'} spacing={isMobile ? 3 : 4}>
            <Grid item={true}>
              <Grid
                container={true}
                direction={'row'}
                alignItems={'center'}
                justifyContent={'space-between'}
                spacing={2}>
                <Grid item={true} xs={true}>
                  <Grid container={true} direction={'row'} alignItems={'center'} spacing={3}>
                    <Grid item={true}>
                      <PageHeader
                        previousFormPath={ROUTE_INBOX}
                        previousFormLabel={'Входящие'}
                        tittle={
                          <Stack direction={'row'} spacing={1}>
                            <span>{'Ответ на письмо №'}</span>
                            <span>{letterLoading ? <Skeleton width={'100px'} height={'32px'} /> : letter?.number}</span>
                          </Stack>
                        }
                        isMobile={isMobile}
                        mobileTittle={
                          <Stack direction={'row'} spacing={1}>
                            <span>{'Ответ на письмо №'}</span>
                            <span>{letterLoading ? <Skeleton width={'100px'} height={'32px'} /> : letter?.number}</span>
                          </Stack>
                        }
                      />
                    </Grid>
                    <Grid item={true} mt={isMobile ? 1 : 0}>
                      <Typography variant={'label1'} sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                        {answerLoading ? (
                          <Skeleton width={'90px'} height={'16px'} />
                        ) : answer ? (
                          formatShortDateTimeString(answer.date)
                        ) : (
                          ''
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                {!isMobile && (
                  <Grid item={true}>
                    <LoadingButton
                      fullWidth={isMobile}
                      variant={'contained'}
                      color={'primary'}
                      sx={(theme: Theme) => ({
                        minWidth: theme.spacing(23),
                      })}
                      isLoading={isSenderLoading}
                      onClick={handleReplyClick}>
                      {'Ответить'}
                    </LoadingButton>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item={true}>
              <Grid container={true} direction={'column'} spacing={4}>
                <Grid item={true}>
                  <Grid container={true} direction={'column'} spacing={3}>
                    <Grid item={true}>
                      <ImagePreview
                        title={`Ответ на письмо № ${answer?.letterNumber}`}
                        subtitle={answer ? formatShortDateTimeString(answer.date) : ''}
                        content={answer?.content || ''}
                        isMobile={isMobile}
                        isLoading={answerLoading}
                      />
                    </Grid>
                    {isMobile && (
                      <Grid item={true}>
                        <LoadingButton
                          fullWidth={isMobile}
                          variant={'contained'}
                          color={'primary'}
                          sx={(theme: Theme) => ({
                            minWidth: theme.spacing(23),
                          })}
                          isLoading={isSenderLoading}
                          onClick={handleReplyClick}>
                          {'Ответить'}
                        </LoadingButton>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item={true}>
                  <Divider />
                </Grid>
                <Grid item={true}>
                  <Grid container={true} direction={'row'} spacing={7}>
                    <Grid item={true} xs={12} sm={9}>
                      <Grid container={true} direction={'column'} spacing={isMobile ? 4 : 5}>
                        <Grid item={true}>
                          <Grid container={true} direction={'column'} spacing={3}>
                            <Grid item={true}>
                              <Grid
                                container={true}
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'space-between'}
                                spacing={2}>
                                <Grid item={true} xs={true}>
                                  <Typography variant={isMobile ? 'h2' : 'h1'} whiteSpace={'nowrap'}>
                                    <Stack direction={'row'} spacing={1}>
                                      <span>{'Письмо №'}</span>
                                      <span>
                                        {letterLoading ? <Skeleton width={'100px'} height={'32px'} /> : letter?.number}
                                      </span>
                                    </Stack>
                                  </Typography>
                                </Grid>
                                <Grid item={true}>
                                  <Typography
                                    variant={'label1'}
                                    sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                                    {letterLoading ? (
                                      <Skeleton width={'90px'} height={'16px'} />
                                    ) : letter ? (
                                      formatShortDateTimeString(letter.sentDate)
                                    ) : (
                                      ''
                                    )}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                            {isMobile && (
                              <Grid item={true}>
                                <MobileLetterHistory events={events} isLoading={eventLogLoading} />
                              </Grid>
                            )}
                            <Grid item={true}>
                              <LetterView
                                letter={letter}
                                isLoading={letterLoading}
                                onAttachFileClick={handleAttachFileClick}
                                onEnclosureClick={(enclosure: EnclosureViewDTO, name: string): void => {
                                  imageView.show(name, enclosure?.content);
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    {!isMobile && (
                      <Grid item={true} xs={3}>
                        <LetterHistory events={events} isLoading={eventLogLoading} />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <ImageViewDialog
        open={imageView.isOpen}
        isLoading={imageView.isLoading}
        isFailed={imageView.isFailed}
        content={imageView.content || ''}
        onPreviousImage={imageView.hasPrevious ? imageView.previous : undefined}
        onNextImage={imageView.hasNext ? imageView.next : undefined}
        title={
          <Grid container={true} direction={'row'} alignItems={'center'} spacing={2}>
            <Grid item={true}>
              <Typography variant={'h1'} fontSize={'24px'} lineHeight={'20px'}>
                {`Вложение письма № ${letter?.number}`}
              </Typography>
            </Grid>
            <Grid item={true}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {imageView.declined !== undefined && <VerdictDot declined={imageView.declined} />}
                <Typography
                  variant={'label2'}
                  fontSize={'12px'}
                  lineHeight={'20px'}
                  sx={{ color: (theme: Theme) => theme.colors.grayText }}>
                  {imageView.name}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        }
        onClose={imageView.close}
      />
    </Fragment>
  );
};
